import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * A Ledgerly screen, drawn rather than photographed.
 *
 * The frame is the design canvas's own device: `402 × 874` logical points at
 * `border-radius: 44px` (spec §0.5). Everything inside is rendered at those
 * exact point values and the whole frame is then scaled with a transform, so
 * the type ladder stays pixel-honest at every breakpoint: a 46px hero is 46px
 * in the frame's coordinate space whether the frame is showing at full size or
 * at 70%.
 *
 * Why HTML and not a screenshot: the frames re-theme with the page, translate
 * with the page, stay sharp on any display, weigh nothing, and cannot go stale
 * the way the old `screenshots/` folder did: every image in it still shows an
 * 0.2.x UI that no longer exists.
 *
 * `--s` is the scale. Set it with Tailwind arbitrary properties on the host,
 * e.g. `[--s:0.62] md:[--s:0.8] xl:[--s:1]`.
 */
@Component({
  selector: 'app-device-frame',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="device-shell"
      role="img"
      [attr.aria-label]="label()"
      [class.device-shell--band]="band()"
    >
      <div class="device" aria-hidden="true">
        <!-- Status bar. Ink stays neutral-100 on every screen that has a band,
             because it sits on the band (spec §F.2.7). -->
        <div class="status">
          <span class="status__time">9:41</span>
          <span class="status__island"></span>
          <span class="status__right">
            <svg viewBox="0 0 18 12" class="status__glyph" aria-hidden="true">
              <rect x="0" y="8" width="3" height="4" rx="1" />
              <rect x="5" y="5.5" width="3" height="6.5" rx="1" />
              <rect x="10" y="3" width="3" height="9" rx="1" />
              <rect x="15" y="0.5" width="3" height="11.5" rx="1" />
            </svg>
            <svg viewBox="0 0 26 12" class="status__glyph" aria-hidden="true">
              <rect
                x="0.5"
                y="0.5"
                width="22"
                height="11"
                rx="3"
                fill="none"
                stroke="currentColor"
                stroke-opacity="0.5"
              />
              <rect x="2.5" y="2.5" width="15" height="7" rx="1.5" />
              <path
                d="M24 4.2v3.6a2 2 0 0 0 0-3.6Z"
                fill="currentColor"
                opacity="0.5"
              />
            </svg>
          </span>
        </div>

        <ng-content />
      </div>
    </div>
  `,
  styles: [
    `
      /* --s is deliberately NOT defaulted on :host. A :host custom-property
         declaration and a [--s:0.88] utility class have the same specificity,
         and Angular injects component styles after the global sheet: so a
         default here would silently win over every caller's scale. The
         fallback lives at each use site instead. */
      :host {
        display: block;
      }

      .device-shell {
        position: relative;
        width: calc(402px * var(--s, 1));
        height: calc(874px * var(--s, 1));
        border-radius: calc(44px * var(--s, 1));
        overflow: hidden;
        background: var(--color-ground);
        /* A shadow in Nocturne is a hairline first and a drop second. */
        box-shadow: var(--shadow-lg);
      }

      /* Frames whose whole screen is the band carry it on the shell too, so
         the rounded corners are indigo rather than ground. */
      .device-shell--band {
        background-image: linear-gradient(
          170deg,
          var(--color-section-glow),
          var(--color-section) 62%,
          color-mix(in srgb, var(--color-section) 70%, #000)
        );
      }

      .device {
        width: 402px;
        height: 874px;
        transform: scale(var(--s, 1));
        transform-origin: top left;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .status {
        position: absolute;
        inset: 0 0 auto 0;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 30px 0;
        color: var(--color-neutral-100);
        font-size: 13px;
        font-weight: 500;
        line-height: 1;
      }

      .status__island {
        width: 88px;
        height: 24px;
        border-radius: 999px;
        background: #000;
        opacity: 0.92;
        margin-top: -6px;
      }

      .status__right {
        display: inline-flex;
        align-items: center;
        gap: 5.6px;
      }

      .status__glyph {
        height: 11px;
        width: auto;
        fill: currentColor;
      }
    `,
  ],
})
export class DeviceFrameComponent {
  /**
   * What the frame shows, for a reader who cannot see it. These are the only
   * strings on the page describing a picture, so they say what the screen
   * answers rather than naming its parts.
   */
  readonly label = input.required<string>();

  /** True when the band fills the whole screen rather than sitting on top. */
  readonly band = input(false);
}
